import {
  alpha,
  createTheme,
  darken,
  PaletteColor,
  PaletteColorOptions,
  ThemeOptions,
} from "@mui/material/styles";
import { orange } from "@mui/material/colors";

// =============================================================================
// 1. EXTENSIÓN DE TIPOS (Module Augmentation)
// =============================================================================

declare module "@mui/material/styles" {
  interface Palette {
    chipColors: {
      [key: string]: string;
    };
    dashboard: {
      main: string;
      secondary: string;
      dark: string;
      light: string;
      contrastText: string;
    };
    orcid: PaletteColor;
  }

  interface PaletteOptions {
    chipColors?: {
      [key: string]: string;
    };
    dashboard?: {
      main: string;
      secondary: string;
      dark: string;
      light: string;
      contrastText: string;
    };
    orcid?: PaletteColorOptions;
  }

  // AÑADIMOS LAS NUEVAS VARIANTES DE TIPOGRAFÍA: subtitle3 y body3
  interface TypographyVariants {
    subtitle3: React.CSSProperties;
    body3: React.CSSProperties;
  }

  // Permitir configuración en theme creation
  interface TypographyVariantsOptions {
    subtitle3?: React.CSSProperties;
    body3?: React.CSSProperties;
  }
}

// Permitir el uso de las variantes en el componente Typography
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    subtitle3: true;
    body3: true;
  }
}

// Extensiones para los componentes (Chip y Badge)
declare module "@mui/material/Chip" {
  interface ChipPropsVariantOverrides {
    borrador: true;
    enviado: true;
    cambiosPreRevisionAplicados: true;
    admitido: true;
    noAdmitido: true;
    editorAsignado: true;
    enRevision: true;
    cambiosMenores: true;
    cambiosPreRevision: true;
    cambiosMenoresAplicados: true;
    cambiosMayores: true;
    cambiosMayoresAplicados: true;
    aceptado: true;
    rechazado: true;
    produccion: true;
    publicado: true;
  }
}

declare module "@mui/material/Badge" {
  interface BadgePropsVariantOverrides {
    counter: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    orcid: true;
  }
}

const DASHBOARD_PRIMARY = "#303F9F";
const DASHBOARD_SECONDARY = "#1976D2";
const DEFAULT_FONT = "Poppins, sans-serif";
const TEXT_PRIMARY = "#333333";
const ORCID_COLOR = "#A6CE39";

const CHIP_COLORS = {
  borrador: "#EEE",
  enviado: "#26C6DA",
  admitido: "#C8E6C9",
  cambiosMenores: "#FFE082",
  cambiosMenoresAplicados: "#FFECB3",
  cambiosMayores: "#FFCC80",
  aceptado: "#66BB6A",
  cambiosMayoresAplicados: "#FFE0B2",
  produccion: "#5C6BC0",
  publicado: "#388E3C",
  rechazado: "#EF5350",
  enRevision: "#42A5F5",
  cambiosPreRevisionAplicados: "#AB47BC",
  cambiosPreRevision: "#AB47BC",
  noAdmitido: "#FECDD2",
  editorAsignado: "#B3E5FC",
};

// =============================================================================
// 3. OPCIONES COMUNES (TIPOGRAFÍA PIXEL-PERFECT)
// =============================================================================

const getCommonThemeOptions = (
  font: string,
  primaryColor: string,
  _secondaryColor: string,
): ThemeOptions => ({
  typography: {
    fontFamily: font,
    h1: {
      fontFamily: font,
      fontSize: "30px",
      fontWeight: 500,
      lineHeight: "115%",
      letterSpacing: "-1.5px",
      color: TEXT_PRIMARY,
    },
    h2: {
      fontFamily: font,
      fontSize: "24px",
      fontWeight: 500,
      lineHeight: "120%",
      letterSpacing: "-0.5px",
      color: TEXT_PRIMARY,
    },
    h3: {
      fontFamily: font,
      fontSize: "21px",
      fontWeight: 500,
      lineHeight: "116.7%",
      color: TEXT_PRIMARY,
    },
    h4: {
      fontFamily: font,
      fontSize: "18px",
      fontWeight: 500,
      lineHeight: "123.5%",
      letterSpacing: "0.25px",
      color: TEXT_PRIMARY,
    },
    h5: {
      fontFamily: font,
      fontSize: "16px",
      fontWeight: 500,
      lineHeight: "133.4%",
      color: TEXT_PRIMARY,
    },
    h6: {
      fontFamily: font,
      fontSize: "16px",
      fontWeight: 500,
      lineHeight: "160%",
      letterSpacing: "0.15px",
      color: TEXT_PRIMARY,
    },
    subtitle1: {
      fontFamily: font,
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "175%",
      letterSpacing: "0.15px",
      color: TEXT_PRIMARY,
    },
    subtitle2: {
      fontFamily: font,
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "157%",
      letterSpacing: "0.1px",
      color: TEXT_PRIMARY,
    },
    subtitle3: {
      fontFamily: font,
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "157%",
      letterSpacing: "0.1px",
      color: TEXT_PRIMARY,
    },
    body1: {
      fontFamily: font,
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "150%",
      letterSpacing: "0.15px",
      color: TEXT_PRIMARY,
    },
    body2: {
      fontFamily: font,
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "143%",
      letterSpacing: "0.17px",
      color: TEXT_PRIMARY,
    },
    body3: {
      fontFamily: font,
      fontSize: "15px",
      fontWeight: 400,
      lineHeight: "150%",
      letterSpacing: "0.15px",
      color: TEXT_PRIMARY,
    },
    caption: {
      fontFamily: font,
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "166%",
      letterSpacing: "0.4px",
      color: TEXT_PRIMARY,
    },
    overline: {
      fontFamily: font,
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "266%",
      letterSpacing: "1px",
      textTransform: "uppercase",
      color: TEXT_PRIMARY,
    },
    button: { fontFamily: font, textTransform: "none", fontWeight: 500 },
  },
  breakpoints: {
    values: { xs: 0, sm: 500, md: 850, lg: 1200, xl: 2220 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
    body { margin: 0; padding: 0; font-family: ${font}; }
    * { box-sizing: border-box; font-family: ${font}; }
  `,
    },
    MuiTypography: {
      styleOverrides: {
        root: { fontFamily: font },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: font,
          fontSize: "14px",
          textTransform: "none",
          borderRadius: "8px", // Movido aquí para que aplique a todos
          "&:active": {
            boxShadow: "0px 1px 10px 0px rgba(0, 0, 0, 0.12)",
          },
        },
        outlined: {
          // Delegamos el color del borde a MUI. Solo añadimos un fondo sutil neutro en hover.
          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
        },
        contained: {
          boxShadow: "none", // Eliminamos la sombra por defecto si lo prefieres plano
          "&.Mui-disabled": {
            backgroundColor: "rgba(0, 0, 0, 0.12)",
            color: "rgba(0, 0, 0, 0.38)",
          },
        },
        text: {
          padding: "9px 0",
          textDecoration: "underline",
          "&:hover": {
            boxShadow: "none",
            backgroundColor: "transparent",
            textDecoration: "underline",
          },
        },
      },
      variants: [
        {
          props: { variant: "contained", color: "orcid" },
          style: {
            backgroundColor: ORCID_COLOR,
            color: "#ffffff", // Fuerza el texto blanco
            "&:hover": {
              backgroundColor: darken(ORCID_COLOR, 0.12),
            },
            "&:active": {
              backgroundColor: darken(ORCID_COLOR, 0.2),
            },
          },
        },
      ],
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: "8px 16px",
          textTransform: "none",
          "&.Mui-selected": { color: primaryColor, borderRadius: "8px" },
          "& .MuiTab-iconWrapper, & span, &": {
            fontFamily: `${font} !important`,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: primaryColor, height: 2 },
        root: {
          padding: "8px 16px",
          textTransform: "none",
          "&.Mui-selected": { color: primaryColor, borderRadius: "8px" },
          "& .MuiTabs-iconWrapper, & span, &": {
            fontFamily: `${font} !important`,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: { fontFamily: font },
        input: { fontFamily: font },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          fontFamily: font,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: font,
          "&.Mui-focused": { color: darken(primaryColor, 0.5) },
          "&.Mui-Hover": { color: darken(primaryColor, 0.5) },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { fontFamily: font },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: { fontFamily: font },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: { fontFamily: font },
        select: { fontFamily: font },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: font,
          backgroundColor: "#ffffff",
          "&:hover": { backgroundColor: alpha(primaryColor, 0.1) },
          "&.Mui-selected": { backgroundColor: "#ffffff" },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        input: { fontFamily: font },
        option: { fontFamily: font },
        noOptions: { fontFamily: font },
        groupLabel: { fontFamily: font },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontFamily: font },
        label: { fontFamily: font },
      },
      variants: [
        {
          props: { variant: "borrador" },
          style: { backgroundColor: CHIP_COLORS.borrador },
        },
        {
          props: { variant: "enviado" },
          style: { backgroundColor: CHIP_COLORS.enviado, color: "#FFF" },
        },
        {
          props: { variant: "admitido" },
          style: { backgroundColor: CHIP_COLORS.admitido },
        },
        {
          props: { variant: "cambiosMenores" },
          style: { backgroundColor: CHIP_COLORS.cambiosMenores },
        },
        {
          props: { variant: "cambiosMenoresAplicados" },
          style: { backgroundColor: CHIP_COLORS.cambiosMenoresAplicados },
        },
        {
          props: { variant: "cambiosMayores" },
          style: { backgroundColor: CHIP_COLORS.cambiosMayores },
        },
        {
          props: { variant: "aceptado" },
          style: { backgroundColor: CHIP_COLORS.aceptado, color: "#FFF" },
        },
        {
          props: { variant: "cambiosMayoresAplicados" },
          style: { backgroundColor: CHIP_COLORS.cambiosMayoresAplicados },
        },
        {
          props: { variant: "produccion" },
          style: { backgroundColor: CHIP_COLORS.produccion, color: "#FFF" },
        },
        {
          props: { variant: "publicado" },
          style: { backgroundColor: CHIP_COLORS.publicado },
        },
        {
          props: { variant: "rechazado" },
          style: { backgroundColor: CHIP_COLORS.rechazado, color: "#FFF" },
        },
        {
          props: { variant: "enRevision" },
          style: { backgroundColor: CHIP_COLORS.enRevision, color: "#FFF" },
        },
        {
          props: { variant: "cambiosPreRevisionAplicados" },
          style: { backgroundColor: CHIP_COLORS.cambiosPreRevisionAplicados },
        },
        {
          props: { variant: "cambiosPreRevision" },
          style: { backgroundColor: CHIP_COLORS.cambiosPreRevision },
        },
        {
          props: { variant: "noAdmitido" },
          style: { backgroundColor: CHIP_COLORS.noAdmitido },
        },
        {
          props: { variant: "editorAsignado" },
          style: { backgroundColor: CHIP_COLORS.editorAsignado },
        },
      ],
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: font,
          backgroundColor: "#ffffff",
          color: "black",
          boxShadow:
            "0px 1px 18px 0px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 3px 5px -1px rgba(0, 0, 0, 0.20)",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { fontFamily: font, borderRadius: "6px", fontSize: "14px" },
        message: { fontFamily: font },
        standardError: {
          backgroundColor: "rgba(211, 47, 47, 0.1)",
          color: "rgba(211, 47, 47, 1)",
        },
        standardSuccess: { backgroundColor: "#EDF7ED", color: "#1E4620" },
        filledSuccess: {
          backgroundColor: "#2E7D32",
          color: "#ffffff",
          "& .MuiAlert-icon": { color: "#ffffff" },
        },
        filledError: {
          backgroundColor: "#d32f2f",
          color: "#ffffff",
          "& .MuiAlert-icon": { color: "#ffffff" },
        },
        filledWarning: {
          backgroundColor: "#ed6c02",
          color: "#ffffff",
          "& .MuiAlert-icon": { color: "#ffffff" },
        },
        filledInfo: {
          backgroundColor: "#0288d1",
          color: "#ffffff",
          "& .MuiAlert-icon": { color: "#ffffff" },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontFamily: font },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { fontFamily: font },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: { fontFamily: font },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { fontFamily: font, height: "23px" },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          fontFamily: font,
          padding: "8px 16px",
          height: "26px",
          "&:hover": { backgroundColor: alpha(primaryColor, 0.05) },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          fontFamily: font,
          "& .MuiTableRow-root:hover": {
            backgroundColor: alpha(primaryColor, 0.05),
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          fontFamily: font,
          backgroundColor: alpha(DASHBOARD_PRIMARY, 0.07),
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: { fontFamily: font, borderRadius: "8px" },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: { fontFamily: font, borderRadius: "8px", fontWeight: 400 },
        secondary: { fontFamily: font },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          fontFamily: font,
          backgroundColor: "#ffffff",
          color: "black",
          boxShadow: "none",
          border: "none",
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          fontFamily: font,
          borderRadius: "8px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          backgroundColor: "#ffffff",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontFamily: font,
          "& fieldset": { borderColor: alpha(primaryColor, 0.5) },
          "&.Mui-focused fieldset": { borderColor: primaryColor },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: primaryColor,
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: { fontFamily: font, fontSize: "inherit", color: "inherit" },
        separator: { color: "inherit" },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontFamily: font,
          textDecoration: "none",
          color: primaryColor,
          fontSize: "16px",
          "&:hover": { color: alpha(primaryColor, 0.5) },
          "&.contrast": { color: darken(primaryColor, 0.5) },
          "&.link": { color: darken(primaryColor, 0.5) },
        },
      },
    },
    MuiBadge: {
      variants: [
        {
          props: { variant: "counter" },
          style: {
            fontFamily: font,
            backgroundColor: "#707070",
            color: "#ffffff",
            height: "20px",
            minWidth: "20px",
            borderRadius: "10px",
            padding: "0 4px",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "12px",
            fontWeight: "500",
          },
        },
      ],
    },
  },
});
// =============================================================================
// 4. CREADOR DEL TEMA DASHBOARD (ESTÁTICO)
// =============================================================================
export const createDashboardTheme = () => {
  const commonOptions = getCommonThemeOptions(
    DEFAULT_FONT,
    DASHBOARD_PRIMARY,
    DASHBOARD_SECONDARY,
  );

  return createTheme({
    ...commonOptions,
    palette: {
      primary: {
        main: DASHBOARD_PRIMARY,
        dark: darken(DASHBOARD_PRIMARY, 0.5),
        contrastText: "#ffffff",
        light: alpha(DASHBOARD_PRIMARY, 0.2),
      },
      secondary: {
        main: DASHBOARD_SECONDARY,
        dark: darken(DASHBOARD_SECONDARY, 0.5),
        light: alpha(DASHBOARD_SECONDARY, 0.2),
      },
      dashboard: {
        main: DASHBOARD_PRIMARY,
        secondary: DASHBOARD_SECONDARY,
        dark: darken(DASHBOARD_PRIMARY, 0.5),
        light: alpha(DASHBOARD_PRIMARY, 0.2),
        contrastText: "#ffffff",
      },
      chipColors: CHIP_COLORS,
      warning: {
        main: "#EF6C00",
        dark: orange[700],
        light: orange[300],
      },
      text: {
        primary: "#333333",
        secondary: "rgba(0, 0, 0, 0.4)",
      },
      action: {
        active: "rgba(0, 0, 0, 0.56)",
        selected: "rgba(92, 107, 192, 0.08)",
        disabledBackground: "rgba(0, 0, 0, 0.02)",
        hover: "rgba(216, 23, 139, 0.08)",
      },
      error: {
        main: "rgba(211, 47, 47, 1)",
        light: "rgba(211, 47, 47, 0.08)",
        dark: "rgba(198, 40, 40, 1)",
        contrastText: "#ffffff",
      },
      success: {
        main: "#66BB6A",
        light: "rgba(46, 125, 50, 0.08)",
        dark: "rgba(46, 125, 50, 1)",
      },
      background: {
        default: "#f4f6f8",
      },
      orcid: {
        main: ORCID_COLOR,
        light: alpha(ORCID_COLOR, 0.3),
        dark: darken(ORCID_COLOR, 0.2),
        contrastText: "#ffffff", // ORCID recomienda blanco sobre su verde para legibilidad
      },
    },
  });
};

// =============================================================================
// 5. CREADOR DEL TEMA REVISTA (DINÁMICO)
// =============================================================================
export const createMagazineTheme = (
  primaryColor: string = DASHBOARD_PRIMARY,
  font: string = DEFAULT_FONT,
) => {
  const commonOptions = getCommonThemeOptions(font, primaryColor, "#1976D2");

  return createTheme({
    ...commonOptions,
    palette: {
      primary: {
        main: primaryColor,
        dark: darken(primaryColor, 0.5),
        contrastText: "#ffffff",
        light: alpha(primaryColor, 0.2),
      },
      secondary: {
        main: "#1976D2",
        dark: darken("#1976D2", 0.5),
        light: alpha("#1976D2", 0.2),
      },
      dashboard: {
        main: DASHBOARD_PRIMARY,
        secondary: DASHBOARD_SECONDARY,
        dark: darken(DASHBOARD_PRIMARY, 0.5),
        light: alpha(DASHBOARD_PRIMARY, 0.2),
        contrastText: "#ffffff",
      },
      chipColors: CHIP_COLORS,
      warning: {
        main: "#EF6C00",
        dark: orange[700],
        light: orange[300],
      },
      error: {
        main: "rgba(211, 47, 47, 1)",
        light: "rgba(211, 47, 47, 0.08)",
        dark: "rgba(198, 40, 40, 1)",
        contrastText: "#ffffff",
      },
      success: {
        main: "#66BB6A",
        light: "rgba(46, 125, 50, 0.08)",
        dark: "rgba(46, 125, 50, 1)",
      },
      background: {
        default: "#ffffff",
      },
      orcid: {
        main: ORCID_COLOR,
        light: alpha(ORCID_COLOR, 0.3),
        dark: darken(ORCID_COLOR, 0.2),
        contrastText: "#ffffff",
      },
      action: {
        hover: alpha(primaryColor, 0.1),
        active: darken(primaryColor, 0.5),
        disabledBackground: "rgba(0, 0, 0, 0.02)",
        selected: alpha(primaryColor, 0.08),
      },
    },
  });
};
